// Lib
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { CellType } from 'hyperformula/es';

// Utils
import { hasCommandModifier, isControlKeyCommand } from '../../../utils/keyboard/keyboardUtility';
import { getFirstSelection, getStartCol, getStartRow } from '../utils/selectionUtils/tableSelectionAccessors';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import {
    hasSelections,
    isSelectingSingleCell,
    isSelectingSingleSelection,
} from '../utils/selectionUtils/tableSelectionPredicateUtils';
import {
    getCellRangeFromStartAndSize,
    getCellSelectionFromRange,
} from '../utils/selectionUtils/tableSelectionRangeUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import { adjustTableContentDataSize } from '../utils/dataUtils/grid/tableGridSizingUtils';
import { normaliseComputedCellValue } from '../utils/dataUtils/cell/tableCellRenderValueUtils';
import { convertCellDataToReduxFormat } from '../utils/dataUtils/persistence/tablePersistenceValueUtils';
import { getTableElementFromHotInstance } from '../utils/domUtils/hotTableDomAccessorUtils';
import HotTableClipboardManager from '../manager/HotTableClipboardManager';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import useRefCallback from '../../../utils/react/useRefCallback';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';
import { buildHotTableContentData } from '../utils/hotInstanceUtils/hotTableDataUtils';
import { inferFormulaCellType } from '../utils/hotInstanceUtils/cell/hotTableFormulaTypeInferenceUtils';
import { convertHotInstanceToRaw } from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';
import { applyRawTableContentToHotInstance } from '../utils/hotInstanceUtils/persistence/hotTableRehydrationUtils';
import {
    createCellDataChanges,
    createCellDataChangesFromSource,
} from '../utils/hotInstanceUtils/cell/hotTableCellEditingUtils';
import { applyHotTableCellMetaInRange } from '../utils/hotInstanceUtils/meta/hotTableMetaRangeUtils';
import { continueCellFormatting } from '../utils/dataUtils/grid/tableGridFormattingContinuationUtils';
import {
    getTableChangeSourceWithTransactionId,
    isTableCellMetaChangeSource,
} from '../../../../common/table/utils/tableChangeSourceUtils';
import { setHotTableCellMetaObject } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { setHotTableCellData } from '../utils/hotInstanceUtils/hotTableUpdateUtils';

// Actions
import { removeSelectedElements } from '../../selection/selectionActions';

// Types
import { TableAxis, TableChangeSource, TableOperation } from '../../../../common/table/TableTypes';

/**
 * Custom hook for handling table cell editing operations
 *
 * @param {Object} props - Hook configuration
 * @returns {Object} Handler functions for table cell editing
 */
export const useTableCellEditingHandlers = ({
    elementId,
    locale,
    gridSize,
    currencyPreference,
    hotTableInstanceRef,
    hotTableContainerRef,
    isSingleSelected,
}) => {
    const dispatch = useDispatch();

    const dispatchDeselectElement = useCallback((elId) => dispatch(removeSelectedElements({ ids: [elId] })), []);

    const beforeAutofill = useRefCallback(
        (_, sourceRange) => {
            HotTableClipboardManager.setTableAutofillSource(
                hotTableInstanceRef,
                getCellSelectionFromRange(sourceRange),
            );
        },
        [hotTableInstanceRef],
    );

    const beforeCopy = useRefCallback(
        (data, coords) => {
            const { startRow, startCol, endRow, endCol } = getFirstSelection(coords);

            HotTableClipboardManager.setTableCopySource(hotTableInstanceRef, [startRow, startCol, endRow, endCol]);
        },
        [hotTableInstanceRef],
    );

    const beforeCut = useRefCallback(
        (data, coords) => {
            beforeCopy(data, coords);

            // Revert readOnly cells to be editable so that they can be cleared on cut
            const { startRow, startCol, endRow, endCol } = getFirstSelection(coords);
            getAllCellsBetween([[startRow, startCol, endRow, endCol]]).forEach(({ row, col }) => {
                hotTableInstanceRef.current.setCellMetaObject(row, col, { readOnly: false });
            });
        },
        [beforeCopy, hotTableInstanceRef],
    );

    const beforePaste = useRefCallback(
        (data, coords) => {
            const { startRow, startCol } = getFirstSelection(coords);

            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            // Revert readOnly cells to be editable so that they can be pasted on to
            const pastedCellSelection = [startRow, startCol, startRow + nRows, startCol + nCols];
            getAllCellsBetween([pastedCellSelection]).forEach(({ row, col }) => {
                hotTableInstanceRef.current.setCellMetaObject(row, col, { readOnly: false });
            });
        },
        [hotTableInstanceRef],
    );

    const beforeChange = useRefCallback(
        (changes, source) => {
            if (!hotTableInstanceRef.current) return;

            // If source is coming from updating cell meta, save data as is
            if (isTableCellMetaChangeSource(source)) return true;

            const data = buildHotTableContentData(hotTableInstanceRef.current);

            // ********************************************
            // Convert cell changes [row, col, prevValue, newValue] to [row, col, prevCellData, newCellData]

            let cellDataChanges;
            if (source === TableChangeSource.AUTOFILL_FILL || source === TableChangeSource.COPY_PASTE_PASTE) {
                const sourceCellDataArray =
                    source === TableChangeSource.AUTOFILL_FILL
                        ? HotTableClipboardManager.getTableAutofillData()
                        : HotTableClipboardManager.getTableCopiedDataForChanges(changes);

                cellDataChanges = createCellDataChangesFromSource(changes, data, source, sourceCellDataArray, locale);
            } else {
                // Retrieve draft editor content from the active editor
                const activeEditor = hotTableInstanceRef.current?.getActiveEditor?.();
                const tableEditorDraftContent = activeEditor?.content;

                cellDataChanges = createCellDataChanges(
                    changes,
                    data,
                    locale,
                    currencyPreference,
                    tableEditorDraftContent,
                );
            }

            // ********************************************
            // Adjust table size to fit all the changes

            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            const maxChangeRow = Math.max(...cellDataChanges.map(([row]) => row)) + 1;
            const maxChangeCol = Math.max(...cellDataChanges.map(([_, col]) => col)) + 1;

            const maxRow = Math.max(maxChangeRow, nRows);
            const maxCol = Math.max(maxChangeCol, nCols);

            const newData = adjustTableContentDataSize(data, maxRow, maxCol);

            const nRowsNew = getRowCount(newData) - nRows;
            const nColsNew = getColumnCount(newData) - nCols;

            const transactionId = getNewTransactionId();
            const sourceWithTransactionId = getTableChangeSourceWithTransactionId(source, transactionId);

            if (nRowsNew > 0) {
                hotTableInstanceRef.current.alter('insert_row_below', nRows - 1, nRowsNew, sourceWithTransactionId);
            }

            if (nColsNew > 0) {
                hotTableInstanceRef.current.alter('insert_col_end', nCols - 1, nColsNew, sourceWithTransactionId);
            }

            // ********************************************
            // Apply changes if any

            cellDataChanges.forEach((cellDataChange, index) => {
                const [row, col, , newCellData] = cellDataChange;

                changes[index][3] = newCellData.value;
                newData[row][col] = newCellData;

                setHotTableCellMetaObject(hotTableInstanceRef.current, row, col, { cellData: newCellData });
            });

            if (isEqual(newData, data)) return;
        },
        [elementId, locale, currencyPreference, hotTableInstanceRef],
    );

    /**
     * After a change is made, for formula cells:
     * 1. infer formula cell's cell type based on precedents cells (if it is not already set)
     * 2. Update the Handsontable instance with those changes **FIXME-TABLES: This might not be required any more?**
     */
    const afterChange = useRefCallback(
        (changes, source) => {
            if (
                !changes ||
                !hotTableInstanceRef.current ||
                source === TableChangeSource.LOAD_DATA ||
                source === TableChangeSource.CELL_META_INIT ||
                source === TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT
            )
                return;

            const sheet = hyperFormulaInstance.getSheetId(elementId);
            if (sheet === undefined) return;

            const data = cloneDeep(buildHotTableContentData(hotTableInstanceRef.current));
            const visited = new WeakMap();

            // These sources should NOT trigger the formula type re-inference for the current cell, but may for dependent cells
            const userTypeUpdateSources = [
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE,
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE_FORMAT,
            ];

            const applyCellChangesForFormulas = (elementId, newChanges, cellAddress, level = 0) => {
                if (visited.get(cellAddress)) return;
                visited.set(cellAddress, true);

                const isDirectUpdateCellTypeChange = userTypeUpdateSources.includes(source) && level === 0;
                const isValidCellAddress = 'row' in cellAddress && 'col' in cellAddress;
                if (!isDirectUpdateCellTypeChange && isValidCellAddress) {
                    const { row, col } = cellAddress;

                    const cellDataChanges = {};
                    const tempCellData = cloneDeep(data[row][col]);

                    // Infer type
                    const currentType = tempCellData.type;
                    const inferredType = inferFormulaCellType(cellAddress, hotTableInstanceRef.current);
                    if (inferredType && inferredType.name !== currentType?.name) {
                        cellDataChanges.type = inferredType;
                        tempCellData.type = inferredType;
                    }

                    // For formulas, save result value so that the cell result value can be indexed and searched
                    if (hyperFormulaInstance.getCellType(cellAddress) === CellType.FORMULA) {
                        const hotComputedCellValue = hyperFormulaInstance.getCellValue(cellAddress);

                        tempCellData.value = normaliseComputedCellValue(hotComputedCellValue, locale, {
                            hfType: hyperFormulaInstance.getCellType(cellAddress),
                            hfDetailedType: hyperFormulaInstance.getCellValueDetailedType(cellAddress),
                        });

                        const currentResultValue = tempCellData.resultValue;
                        const resultValue = convertCellDataToReduxFormat(tempCellData, locale).value;

                        if (resultValue !== undefined && resultValue !== currentResultValue) {
                            cellDataChanges.resultValue = resultValue;
                        }
                    }
                }

                const cellDependents = hyperFormulaInstance.getCellDependents(cellAddress);
                cellDependents.forEach((cellDependent) => {
                    applyCellChangesForFormulas(elementId, newChanges, cellDependent, level + 1);
                });
            };

            const cellDataChanges = [];

            changes.forEach(([row, col]) =>
                applyCellChangesForFormulas(elementId, cellDataChanges, { sheet, row, col }),
            );

            if (cellDataChanges.length === 0) return;

            // FIXME-TABLES - Check with Marisa what the purpose of this function is
            //  It'd be nice to just rely on the useHotToReduxSession afterChange, rather than
            //  manually triggering the update here
            //  - I wasn't able to determine how to trigger a cell type update based on a formula

            setHotTableCellData(
                hotTableInstanceRef.current,
                cellDataChanges,
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT,
            );
        },
        [elementId, locale, hotTableInstanceRef],
    );

    /**
     * This function is called we want to sync table data from Handsontable over to table data in Redux.
     */
    const afterGridAltered = useRefCallback(
        (axis, operation, index, amount, source) => {
            // Need to recalculate the autofill handle position after grid altered
            // e.g. bottom right corner may have previously been selected but isn't
            // anymore since rows have been added below
            repositionAutofillHandle(hotTableInstanceRef.current, hotTableContainerRef);

            if (!hotTableInstanceRef.current) return;

            const rawTableContent = convertHotInstanceToRaw(hotTableInstanceRef.current, gridSize);

            let formattedData = rawTableContent.data;

            if (operation === TableOperation.INSERT) {
                formattedData = continueCellFormatting({
                    dataArray: rawTableContent.data,
                    colIndex: axis === TableAxis.COL ? index : 0,
                    nColsAdded: axis === TableAxis.COL ? amount : 0,
                    rowIndex: axis === TableAxis.ROW ? index : 0,
                    nRowsAdded: axis === TableAxis.ROW ? amount : 0,
                });

                const nRows = formattedData.length;
                const nCols = formattedData[0]?.length || 0;

                applyHotTableCellMetaInRange({
                    hotInstance: hotTableInstanceRef.current,
                    cellRange: getCellRangeFromStartAndSize(
                        axis === TableAxis.COL
                            ? { start: { row: 0, col: index }, size: { row: nRows, col: amount } }
                            : { start: { row: index, col: 0 }, size: { row: amount, col: nCols } },
                    ),
                    getCellMeta: ({ row, col }) => ({
                        cellData: formattedData[row][col],
                    }),
                });
            }
        },
        [hotTableInstanceRef, hotTableContainerRef, gridSize],
    );

    const afterGridInsert = useRefCallback(
        (axis) => (index, amount, source) => {
            // Make sure all cells have a value in Hyperformula, otherwise we will get
            // an error when trying to perform operations like setIndexesSequence
            // once https://github.com/handsontable/handsontable/issues/10569 has been resolved,
            // check if we can remove this
            const sheetId = hyperFormulaInstance.getSheetId(elementId);
            const nCols = hotTableInstanceRef.current.countCols();
            const nRows = hotTableInstanceRef.current.countRows();

            // Set new cells to empty in Hyperformula
            if (axis === TableAxis.COL) {
                const newContentSingleRow = new Array(amount).fill('');
                const newContent = new Array(nRows).fill(newContentSingleRow);
                const firstNewCellAddress = { sheet: sheetId, row: 0, col: index };
                hyperFormulaInstance.setCellContents(firstNewCellAddress, newContent);
            } else if (axis === TableAxis.ROW) {
                const newContentSingleRow = new Array(nCols).fill('');
                const newContent = new Array(amount).fill(newContentSingleRow);
                const firstNewCellAddress = { sheet: sheetId, row: index, col: 0 };
                hyperFormulaInstance.setCellContents(firstNewCellAddress, newContent);
            }

            afterGridAltered(axis, TableOperation.INSERT, index, amount, source);
        },
        [afterGridAltered],
    );

    const afterGridRemove = useRefCallback(
        (axis) => (index, amount, _, source) => {
            afterGridAltered(axis, TableOperation.REMOVE, index, amount, source);
        },
        [afterGridAltered],
    );

    /**
     * In Handsontable, moving rows/columns is treated as a temporary visual change, and only persist in the
     * session. The ordering is saved in `hotTableInstanceRef.current.rowIndexMapper.indexesSequence`.
     *
     * Since the persisted snapshot still stores row/column order directly in the table data, we rewrite HOT's
     * source data to the currently visible moved order and then reset the index mapper back to identity.
     * The active-table persistence observer then persists that settled HOT state via the normal debounced path.
     */
    // TODO-TABLES - WEB-15538 - We'll attempt to persist the indexes, rather than rewriting all the data
    //   on column moves
    const afterGridMovement = useRefCallback(
        (axis) => (movedIndices, finalIndex, dropIndex, movePossible, orderChanged) => {
            if (!movePossible || !orderChanged) return;
            if (!hotTableInstanceRef.current) return;

            const movedIndicesCount = movedIndices.length;
            const rawTableContent = convertHotInstanceToRaw(hotTableInstanceRef.current, gridSize);

            if (axis === TableAxis.ROW) {
                const nRows = hotTableInstanceRef.current.countRows();
                const originalIndexesSequence = new Array(nRows).fill(1).map((_, i) => i);
                hotTableInstanceRef.current.rowIndexMapper.setIndexesSequence(originalIndexesSequence);
            }

            if (axis === TableAxis.COL) {
                const nCols = hotTableInstanceRef.current.countCols();
                const originalIndexesSequence = new Array(nCols).fill(1).map((_, i) => i);
                hotTableInstanceRef.current.columnIndexMapper.setIndexesSequence(originalIndexesSequence);
            }

            applyRawTableContentToHotInstance({
                hotInstance: hotTableInstanceRef.current,
                tableContent: rawTableContent,
                gridSize,
            });

            // Update the selection to match the newly moved cell locations
            // NOTE: Not 100% sure why we need to wait an animation frame for this.
            //  In the current code it's still required, but perhaps there's a cleaner solution that doesn't need it
            requestAnimationFrame(() => {
                if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

                hotTableInstanceRef.current.deselectCell();

                const startSelectIndex = finalIndex;
                const endSelectIndex = finalIndex + movedIndicesCount - 1;

                if (axis === TableAxis.ROW) hotTableInstanceRef.current.selectRows(startSelectIndex, endSelectIndex);
                if (axis === TableAxis.COL) hotTableInstanceRef.current.selectColumns(startSelectIndex, endSelectIndex);
            });
        },
        [gridSize, hotTableInstanceRef],
    );

    const beforeOnCellMouseDown = useRefCallback(
        (event, coords) => {
            // @ts-ignore - Handsontable private property
            if (event.isImmediatePropagationEnabled === false) return;

            // If user clicks on a checkbox in a cell, don't select the cell
            if (event.target.parentElement.classList.contains('checkbox-animated')) {
                event.isImmediatePropagationEnabled = false;
            }

            if (event.target.parentElement.parentElement.classList.contains('DraftLinkAnchor')) {
                // If a link has been clicked, don't select the cell
                event.stopImmediatePropagation();
                event.preventDefault();
                return false;
            }

            if (!isSingleSelected) {
                // If the table element is not yet selected, select the table element instead of the cell
                // this would only happen when the cover is not present (e.g. when the table is in mobile view)
                const tableElement = getTableElementFromHotInstance(hotTableInstanceRef.current);
                if (!tableElement) return;

                // Both events are necessary to trigger the selection of the table element
                // In ElementContainer mouseDown alone will never select the element as there is an early
                // return for document mode, and click will only run if there has been a mouseDown event
                tableElement.dispatchEvent(new MouseEvent('mousedown', event));
                tableElement.click();
                return;
            }

            const isRightMouseClick = event.button === 2;
            if (isRightMouseClick) return;

            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            // ************************************************
            // On Cmd+click, and no cells are selected, deselect element

            if ((hasCommandModifier(event) || isControlKeyCommand(event)) && !hasSelections(hotCurrentCellSelections)) {
                event.stopImmediatePropagation();
                event.preventDefault();

                dispatchDeselectElement(elementId);
                return;
            }

            // ************************************************
            // On clicking the selected cell one more time, immediately go to edit mode. This overrides the default
            // behaviour of requiring a double click to edit a cell, and is more in line with Milanote editing process.

            const isSelectingCurrentlySelectedCell =
                isSelectingSingleSelection(hotCurrentCellSelections) &&
                isSelectingSingleCell(getFirstSelection(hotCurrentCellSelections)) &&
                coords.row === getStartRow(getFirstSelection(hotCurrentCellSelections)) &&
                coords.col === getStartCol(getFirstSelection(hotCurrentCellSelections));

            if (
                !(hasCommandModifier(event) || isControlKeyCommand(event)) &&
                isSelectingCurrentlySelectedCell &&
                hotTableInstanceRef.current.getActiveEditor()
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                hotTableInstanceRef.current.getActiveEditor().beginEditing();
            }
        },
        [elementId, isSingleSelected, hotTableInstanceRef, dispatchDeselectElement],
        true,
    );

    return {
        beforeAutofill,
        beforeCopy,
        beforeCut,
        beforePaste,
        beforeChange,
        afterChange,
        afterGridInsert,
        afterGridRemove,
        afterGridMovement,
        beforeOnCellMouseDown,
    };
};

export default useTableCellEditingHandlers;
